import { ContainerClient } from "@azure/storage-blob";
import { OrganizationRoles, ProjectRoles } from "./Enums";
import { EventHistoryStatus } from "./ObjectTypes";
import tailwindClassnames from "./TailwindClassnames";

/**
 * Maps an import/upload event's status to its status-chip Tailwind class
 * (see chipActive/chipInProgress/chipFailed/chipInactive in TailwindClassnames.tsx).
 */
export function eventStatusChipClass(status: EventHistoryStatus): string {
  switch (status) {
    case EventHistoryStatus.Completed:
      return tailwindClassnames.chipActive;
    case EventHistoryStatus.InProgress:
      return tailwindClassnames.chipInProgress;
    case EventHistoryStatus.Failed:
      return tailwindClassnames.chipFailed;
    case EventHistoryStatus.Cancelled:
    default:
      return tailwindClassnames.chipInactive;
  }
}

export function getSendOrRemoveInvitePermissionsFromOrgRole(
  role: number,
  perms: any[],
) {
  switch (role) {
    case OrganizationRoles.OWNER:
      if (
        perms.some((perm: any) => perm.name.includes("INVITE_USER_AS_OWNER"))
      ) {
        return true;
      }
      return false;
    case OrganizationRoles.EDITOR:
    case OrganizationRoles.CONTRIBUTOR:
      if (
        perms.some((perm: any) =>
          perm.name.includes("INVITE_USER_AS_EDITOR_CONTRIBUTOR"),
        )
      ) {
        return true;
      }
      return false;
    case OrganizationRoles.VIEWER:
      if (
        perms.some((perm: any) => perm.name.includes("INVITE_USER_AS_VIEWER"))
      ) {
        return true;
      }
      return false;

    default:
      return false;
  }
}

export function getSendOrRemoveInvitePermissionFromProjectRole(
  role: number,
  perms: any[],
) {
  // only allow users to resend invitations if they have the correct permissions
  switch (role) {
    case ProjectRoles.OWNER:
    case ProjectRoles.EDITOR:
      if (
        perms.some((perm: any) => perm.name.includes("INVITE_OWNERS_EDITORS"))
      ) {
        return true;
      }
      return false;
    case ProjectRoles.VIEWER:
    case ProjectRoles.CONTRIBUTOR:
    case ProjectRoles.TAGGER:
      if (
        perms.some((perm: any) =>
          perm.name.includes("INVITE_VIEWERS_CONTRIBUTORS_TAGGERS"),
        )
      ) {
        return true;
      }
      return false;
    default:
      return false;
  }
}
/**
 * Gets a file name with nested subfolder structure included in the name.
 * The root folder (top-level folder selected by user) is excluded from the filename.
 * Only nested subfolders within the root are included.
 * Examples:
 * - Folder1/image1.jpeg → image1.jpeg (no subfolders)
 * - Folder1/SubfolderA/image1.jpeg → SubfolderA_image1.jpeg (one subfolder)
 * - Folder1/SubA/SubB/image1.jpeg → SubA_SubB_image1.jpeg (multiple subfolders)
 * @param file - The file object to get the name from.
 * @returns the file name with nested subfolder names included if applicable.
 */
export function getFileName(file: File) {
  const relativePath = file.webkitRelativePath;
  const parts = relativePath.split("/");
  let displayName = file.name;
  if (parts.length > 2) {
    // If there are nested subfolders (more than just root folder + file),
    // prefix the file name with subfolder names (excluding root) joined by underscores
    const subfolder = parts.slice(1, -1).join("_");
    if (subfolder.length > 0) {
      displayName = `${subfolder}_${file.name}`;
    }
  }
  return displayName;
}

/** Files in one selection that would be stored under the same blob name (AKL-588). */
export interface FlattenedNameCollision {
  /** The flattened name, as the first of these files spells it. */
  name: string;
  /** Each clashing file's path inside the chosen folder, in selection order. */
  paths: string[];
}

/**
 * Finds the files in a selection that `getFileName` would flatten to the same
 * blob name, so an upload can refuse the folder before anything is sent
 * (AKL-588). Two different files under one name are written to one blob and the
 * second replaces the first: `Card_A/IMG_0001.JPG` and `Card/A_IMG_0001.JPG`
 * both become `Card_A_IMG_0001.JPG` (seen losing a photo in production, 29
 * September 2026).
 *
 * Only the files the upload would actually send are compared: images, and not
 * dot-files. Keep those two rules in step with the skips at the top of
 * `AzureUploadManager.runPass`. Names are compared ignoring upper and lower
 * case, as Akili Transfer's check does (`src/upload/flatten.ts` there), so the
 * same folder is refused by both tools and an export downloaded to Windows or
 * macOS can never hold two spellings of one name.
 *
 * @param files the selection, in the order the browser gave it
 * @returns every name shared by two or more files, in first-seen order; empty
 *   when there is no collision
 */
export function findFlattenedNameCollisions(
  files: readonly File[],
): FlattenedNameCollision[] {
  const groups = new Map<string, FlattenedNameCollision>();
  for (const file of files) {
    if (!file.type.startsWith("image/") || file.name.startsWith(".")) {
      continue;
    }
    // A browser always gives `webkitRelativePath` (empty outside a folder pick);
    // only a test double may lack it. `getFileName` itself stays untouched: it is
    // the AKL-432 parity function and must not change.
    const relativePath: unknown = file.webkitRelativePath;
    const name =
      typeof relativePath === "string" ? getFileName(file) : file.name;
    const key = name.toLowerCase();
    const path =
      typeof relativePath === "string" && relativePath !== ""
        ? relativePath
        : file.name;
    const group = groups.get(key);
    if (group) {
      group.paths.push(path);
    } else {
      groups.set(key, { name, paths: [path] });
    }
  }
  return Array.from(groups.values()).filter((group) => group.paths.length > 1);
}

/**
 * Convert a local Date object to YYYY-MM-DD string in local time.
 * @param date The local Date object
 * @returns Local date string in YYYY-MM-DD format
 */
export function formatDateAsLocalString(date: Date | null): string {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // 0-indexed
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD string as a local Date (midnight local time).
 * @param dateString string in "YYYY-MM-DD" format
 * @returns Date object at local midnight
 */
export function parseLocalDateStringToDate(
  dateString: string | null,
): Date | null {
  if (dateString === null) return null;
  if (typeof dateString !== "string") {
    console.error("Invalid date string:", dateString);
    return null;
  }
  const date = new Date(`${dateString}T00:00:00`); // Append time to ensure it's interpreted as local time
  return date;
}

/**
 * If `raw` is a complete year (4 digits, with or without its trailing dash —
 * "2020" or "2020-") or a complete year+month (a full 2-digit month, with or
 * without its own trailing dash — "2020-01"/"2020-01-" — or any 1-2 digit
 * month once its dash confirms it's done — "2020-1-"), returns the first of
 * that month/year. Used by the site's date pickers to snap the open calendar
 * to what's being typed, before the date is actually complete.
 *
 * A lone 1-digit month with no dash yet ("2020-1") is deliberately not
 * matched here — it's ambiguous (still could become "10"/"11"/"12"), so
 * live-typing can't safely snap to it; see preSelectionFromTypedYearMonth,
 * the Enter-triggered counterpart that handles that case once the reviewer
 * explicitly submits it.
 * @param raw the date input's current raw typed text
 * @returns the first of the typed month/year, or null if `raw` isn't a
 *   complete year/year+month
 */
export const preSelectionFromTypedPrefix = (raw: string): Date | null => {
  const yearOnly = /^(\d{4})-?$/.exec(raw);
  if (yearOnly) return new Date(Number(yearOnly[1]), 0, 1);

  const yearMonth = /^(\d{4})-(?:(\d{2})|(\d{1,2})-)$/.exec(raw);
  if (yearMonth) {
    const month = Number(yearMonth[2] ?? yearMonth[3]);
    if (month >= 1 && month <= 12) {
      return new Date(Number(yearMonth[1]), month - 1, 1);
    }
  }
  return null;
};

/**
 * If `raw` is exactly a year+month with no trailing day ("2020-1"/"2020-01"),
 * returns the first of that month/year. Used on Enter, since the second
 * dash never arrives when a month is submitted directly (see
 * preSelectionFromTypedPrefix, its live-typing counterpart).
 * @param raw the date input's current raw typed text
 * @returns the first of the typed month/year, or null if `raw` isn't
 *   exactly a year+month
 */
export const preSelectionFromTypedYearMonth = (raw: string): Date | null => {
  const yearMonth = /^(\d{4})-(\d{1,2})$/.exec(raw);
  if (yearMonth) {
    const month = Number(yearMonth[2]);
    if (month >= 1 && month <= 12) {
      return new Date(Number(yearMonth[1]), month - 1, 1);
    }
  }
  return null;
};

/**
 * Formats a calendar day as "YYYY-MM-DD" without ever shifting it by the
 * browser's time zone (AKL-828).
 * - A string that already starts with "YYYY-MM-DD" is returned as those ten
 *   characters, untouched, if they name a real calendar day ("2026-02-30"
 *   and "2026-99-99" return "").
 * - A Date (e.g. the local-midnight Date a date picker emits) is formatted
 *   from its local year, month and day, never from UTC: `toISOString()` turns
 *   local midnight 1 Sep into 31 Aug for anyone ahead of UTC.
 * - Empty or unparseable input returns "".
 * @param value The day to format: a date-only string or a Date.
 * @returns The day in "YYYY-MM-DD" format, or "".
 */
export const formatDateToYYYYMMDD = (
  value: string | Date | null | undefined,
): string => {
  if (!value) return "";
  if (typeof value === "string") {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (dateOnly) {
      const [, year, month, day] = dateOnly.map(Number);
      // Pure calendar check, so UTC: a local check would reject a day a zone
      // skipped (Pacific/Apia has no 2011-12-30). setUTCFullYear, not
      // Date.UTC: that maps years 0-99 to the 1900s.
      const check = new Date(0);
      check.setUTCFullYear(year, month - 1, day);
      const isRealDay =
        check.getUTCFullYear() === year &&
        check.getUTCMonth() === month - 1 &&
        check.getUTCDate() === day;
      return isRealDay ? dateOnly[0] : "";
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDateAsLocalString(date);
};

/**
 * Formats a timestamp (in seconds) to international format "YYYY-MM-DD HH:MM:SS" using 24-hour clock.
 * @param timestamp The timestamp in seconds (Unix timestamp), or null if not yet known (e.g. an activity that hasn't started/ended).
 * @returns The formatted date string in "YYYY-MM-DD HH:MM:SS" format, or "" when timestamp is null.
 */
export const formatTimestampToInternational = (
  timestamp: number | null,
): string => {
  if (timestamp === null || timestamp === undefined) return "";
  const date = new Date(timestamp * 1000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export async function countBlobsForDeployment(
  containerSasUrl: string,
  deploymentGuid: string,
): Promise<number> {
  const containerClient = new ContainerClient(containerSasUrl);
  const folderPrefix = `deployment-${deploymentGuid}/`;
  let count = 0;

  try {
    // Use byPage to iterate through blobs efficiently
    const pageIterator = containerClient
      .listBlobsFlat({ prefix: folderPrefix })
      .byPage({ maxPageSize: 1000 });

    for await (const response of pageIterator) {
      count += response.segment.blobItems.length;
    }
  } catch (error) {
    console.error(
      `Error counting blobs for deployment ${deploymentGuid}:`,
      error,
    );
  }

  return count;
}

/**
 * Format a byte count for display, e.g. "12.4 MB".
 * @param bytes the size in bytes, or null
 * @returns a human-readable size, or an empty string when unknown
 */
export const formatBytes = (bytes: number | null): string => {
  if (bytes === null || bytes === undefined) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
};
